import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, HotelStars, Icon, Modal, Text } from "@zorni/components-lib";

import styles from "./ModalTripTimelineItem.module.css";
import { currentLocale } from "../../../../common/i18n/locale";

const isRenderableImage = (src) =>
  typeof src === "string" && /^(https?:\/\/|\/)/i.test(src.trim());

const priceOf = (price) => ({
  value: Number(price?.value ?? price?.amount ?? 0) || 0,
  currency: price?.currency ?? "",
});

/** « 450 MAD », sans décimales inutiles. */
export const formatPrice = (price) => {
  const { value, currency } = priceOf(price);
  return `${value.toLocaleString(currentLocale())}${currency ? ` ${currency}` : ""}`;
};

const itemTitle = (item) =>
  typeof item === "string" ? item : item?.title ?? item?.name ?? item?.label ?? "";

const itemPicture = (item) =>
  typeof item === "string"
    ? ""
    : item?.picture ?? item?.image ?? item?.photo?.link ?? item?.pictures?.[0]?.link ?? "";

/** Normalise activités et options en une même forme. */
const normalize = (item, type, index) => {
  const base = typeof item === "string" ? { title: item } : item || {};
  return {
    id: String(base?.id ?? base?.title ?? `${type}-${index}`),
    type,
    title: itemTitle(base),
    picture: itemPicture(base),
    description: base?.description ?? "",
    isIncluded: base?.isIncluded === true,
    adult: priceOf(base?.adultPricePerPerson),
    child: priceOf(base?.childPricePerPerson),
  };
};

/** Vignette d'un supplément, avec un repère neutre si la photo manque. */
const Thumb = ({ src, alt, className, emptyClassName }) => {
  const [broken, setBroken] = useState(false);
  const usable = isRenderableImage(src) && !broken;

  return (
    <span className={usable ? className : `${className} ${emptyClassName || ""}`.trim()}>
      {usable ? (
        <img src={src} alt={alt} onError={() => setBroken(true)} loading="lazy" />
      ) : (
        <span className={styles.noPhoto} aria-hidden="true">
          <Icon name="iconCamera" size="md" color="muted" />
        </span>
      )}
    </span>
  );
};

Thumb.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  emptyClassName: PropTypes.string,
};

/**
 * Personnalisation d'une étape : ce qui est déjà inclus, puis les activités
 * payantes et les options à ajouter, avec le montant du supplément.
 * Le brouillon n'est appliqué (setAcquisitions) qu'à l'enregistrement.
 */
const ModalTripTimelineItem = ({
  tripProgramId,
  city,
  picture,
  description,
  isOpen,
  onClose,
  activities = [],
  options = [],
  setAcquisitions,
  acquisitions,
  hostingName,
  hostingStars,
  stepNumber,
  stepTotal,
  dateLabel,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (isOpen) setDraft(acquisitions || {});
  }, [acquisitions, isOpen]);

  const { included, addons } = useMemo(() => {
    const list = [
      ...(Array.isArray(activities) ? activities : []).map((a, i) => normalize(a, "activity", i)),
      ...(Array.isArray(options) ? options : []).map((o, i) => normalize(o, "option", i)),
    ];
    return {
      included: list.filter((item) => item.isIncluded),
      addons: list.filter((item) => !item.isIncluded),
    };
  }, [activities, options]);

  const quantityOf = useCallback(
    (item, travelerType) => Number(draft?.[item.type]?.[item.id]?.[travelerType] || 0),
    [draft]
  );

  /* Une quantité à 0 retire l'entrée : AcquisitionsHelper compte les clés. */
  const changeQuantity = (item, travelerType, delta) => {
    setDraft((previous) => {
      const group = { ...(previous?.[item.type] || {}) };
      const current = group[item.id] || { adult: 0, child: 0 };
      const next = {
        ...current,
        [travelerType]: Math.max(0, Number(current[travelerType] || 0) + delta),
        tripProgramId,
      };

      if (!next.adult && !next.child) delete group[item.id];
      else group[item.id] = next;

      return { ...previous, [item.type]: group };
    });
  };

  const selection = useMemo(
    () =>
      addons
        .map((item) => {
          const adult = quantityOf(item, "adult");
          const child = quantityOf(item, "child");
          return {
            item,
            adult,
            child,
            amount: item.adult.value * adult + item.child.value * child,
          };
        })
        .filter((line) => line.adult + line.child > 0),
    [addons, quantityOf]
  );

  const total = selection.reduce((sum, line) => sum + line.amount, 0);
  const currency = addons[0]?.adult.currency || addons[0]?.child.currency || "";

  const handleSave = () => {
    setAcquisitions?.(draft);
    onClose?.();
  };

  if (!isOpen) return null;

  const travelersLabel = ({ adult, child }) =>
    [
      adult ? `${adult} ${t(adult > 1 ? "Traveler.Content.Adults" : "Traveler.Content.Adult", adult > 1 ? "adultes" : "adulte").toLowerCase()}` : "",
      child ? `${child} ${t(child > 1 ? "Traveler.Content.Childs" : "Traveler.Content.Child", child > 1 ? "enfants" : "enfant").toLowerCase()}` : "",
    ]
      .filter(Boolean)
      .join(" · ");

  const stepper = (item, travelerType, value, label) => (
    <span className={styles.stepper}>
      <button
        type="button"
        className={styles.stepBtn}
        onClick={() => changeQuantity(item, travelerType, -1)}
        disabled={!value}
        aria-label={`${t("action.remove", "Retirer")} ${label} – ${item.title}`}
      >
        <Icon name="iconMinus" size="sm" />
      </button>
      <span className={styles.stepValue}>{value}</span>
      <button
        type="button"
        className={`${styles.stepBtn} ${styles.plus}`}
        onClick={() => changeQuantity(item, travelerType, 1)}
        aria-label={`${t("action.add", "Ajouter")} ${label} – ${item.title}`}
      >
        <Icon name="iconPlus" size="sm" />
      </button>
    </span>
  );

  return (
    <Modal t={t}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      variant="plain"
      mobileSheet
      className={styles.panel}
      ariaLabel={`${t("commons.buttons.personalize", "Personnaliser")} – ${city || ""}`.trim()}
    >
      <div className={styles.layout}>
        {/* ---------------------------------------------- Colonne de gauche */}
        <div className={styles.photo}>
          {isRenderableImage(picture) ? (
            <img src={picture} alt="" />
          ) : (
            <span className={styles.photoFallback} aria-hidden="true" />
          )}
          <div className={styles.photoTitle}>
            {Number(stepTotal) > 0 && (
              <span className={styles.stepNo}>
                {t("tripDetails.timeline.step", "Étape")} {stepNumber} / {stepTotal}
              </span>
            )}
            <Text as="h2" className={styles.stepTitle}>
              {city || t("TripDetails", "Détail du voyage")}
            </Text>
          </div>
        </div>

        <div className={styles.info}>
          <div className={styles.facts}>
            {dateLabel && (
              <div className={styles.fact}>
                <i>
                  <Icon name="iconCalendar" size="sm" />
                </i>
                {dateLabel}
              </div>
            )}
            {hostingName && (
              <div className={styles.fact}>
                <i>
                  <Icon name="iconHotel" size="sm" />
                </i>
                <span className={styles.hostName} title={hostingName}>
                  {hostingName}
                </span>
                {!!hostingStars && (
                  <HotelStars
                    value={Number(hostingStars)}
                    starCount={Number(hostingStars)}
                    color="black"
                    emptyColor="muted"
                    allowHalf={false}
                  />
                )}
              </div>
            )}
          </div>

          {!!description && <p className={styles.description}>{description}</p>}

          <div className={styles.selection}>
            <h3 className={styles.blockTitle}>
              {t("tripDetails.step.selection", "Votre sélection")}
            </h3>
            {selection.length ? (
              <ul className={styles.selectionList}>
                {selection.map((line) => (
                  <li key={`${line.item.type}-${line.item.id}`}>
                    <Thumb src={line.item.picture} alt="" className={styles.selectionThumb} />
                    <span>
                      <strong>{line.item.title}</strong>
                      <small>{travelersLabel(line)}</small>
                    </span>
                    <b>{formatPrice({ value: line.amount, currency })}</b>
                  </li>
                ))}
              </ul>
            ) : null}

            {selection.length === 0 && (
              <p className={styles.empty}>
                {t(
                  "tripDetails.step.noSelection",
                  "Aucun supplément pour le moment. Les activités incluses sont déjà comprises dans le prix du voyage."
                )}
              </p>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <p className={styles.total}>
            <small>{t("tripDetails.step.extra", "Supplément")}</small>
            <strong>{formatPrice({ value: total, currency })}</strong>
          </p>
          <div className={styles.actions}>
            <Button variant="secondary" size="medium" label={t("action.undo")} onClick={onClose} />
            <Button variant="primary" size="medium" label={t("action.save")} onClick={handleSave} />
          </div>
        </div>

        {/* ---------------------------------------------- Colonne de droite */}
        <div className={styles.main}>
          <div className={styles.mainHead}>
            <div>
              <Text as="h3" className={styles.mainTitle}>
                {t("tripDetails.step.title", "Personnalisez votre journée")}
              </Text>
              <p className={styles.mainSubtitle}>
                {t(
                  "tripDetails.step.subtitle",
                  "Ajoutez des activités ou des options à cette étape."
                )}
              </p>
            </div>
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label={t("action.close", "Fermer")}
            >
              <Icon name="iconClose" size="md" />
            </button>
          </div>

          {addons.length > 0 && (
            <section className={styles.sectionAddons}>
              <h4 className={styles.sectionTitle}>
                {t("tripDetails.step.toAdd", "À ajouter")}
                {selection.length > 0 && (
                  <span className={styles.count}>
                    {" · "}
                    {t("tripDetails.step.chosen", { count: selection.length, defaultValue: "{{count}} choisi" })}
                  </span>
                )}
              </h4>
              <div className={styles.cards}>
                {addons.map((item) => {
                  const adult = quantityOf(item, "adult");
                  const child = quantityOf(item, "child");
                  const selected = adult + child > 0;

                  return (
                    <article
                      key={`${item.type}-${item.id}`}
                      className={`${styles.card} ${selected ? styles.cardSelected : ""}`}
                    >
                      <Thumb
                        src={item.picture}
                        alt=""
                        className={styles.cardMedia}
                        emptyClassName={styles.cardMediaEmpty}
                      />
                      <span className={styles.type}>
                        {item.type === "option"
                          ? t("tripDetails.step.option", "Option")
                          : t("tripDetails.step.activity", "Activité")}
                      </span>
                      {selected && (
                        <span className={styles.check} aria-hidden="true">
                          <Icon name="iconCheck" size="sm" />
                        </span>
                      )}

                      <div className={styles.cardBody}>
                        <h5>{item.title}</h5>
                        {!!item.description && <p>{item.description}</p>}
                      </div>

                      <div className={styles.cardFoot}>
                        <div className={`${styles.qtyRow} ${adult ? styles.qtyOn : ""}`}>
                          <span>
                            <b>{formatPrice(item.adult)}</b>
                            {t("tripDetails.step.perAdult", "par adulte")}
                          </span>
                          {stepper(item, "adult", adult, t("Traveler.Content.Adult", "adulte"))}
                        </div>
                        {item.child.value > 0 ? (
                          <div className={`${styles.qtyRow} ${child ? styles.qtyOn : ""}`}>
                            <span>
                              <b>{formatPrice(item.child)}</b>
                              {t("tripDetails.step.perChild", "par enfant")}
                            </span>
                            {stepper(item, "child", child, t("Traveler.Content.Child", "enfant"))}
                          </div>
                        ) : (
                          <div className={styles.qtyRow}>
                            <span className={styles.na}>
                              {t("tripDetails.step.noChildPrice", "Non proposé aux enfants")}
                            </span>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {included.length > 0 && (
            <section className={styles.sectionIncluded}>
              <h4 className={styles.sectionTitle}>
                {t("tripDetails.step.included", "Déjà inclus")}
              </h4>
              <div className={styles.includedList}>
                {included.map((item) => (
                  <div key={`${item.type}-${item.id}`} className={styles.inc}>
                    <Thumb src={item.picture} alt="" className={styles.incThumb} />
                    <div>
                      <span className={styles.incTag}>
                        <Icon name="iconCheck" size="sm" />
                        {t("details.included", "Inclus")}
                      </span>
                      <strong>{item.title}</strong>
                      {!!item.description && <small>{item.description}</small>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!addons.length && !included.length && (
            <p className={styles.empty}>
              {t("tripDetails.step.nothing", "Aucune activité ni option pour cette étape.")}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

ModalTripTimelineItem.propTypes = {
  tripProgramId: PropTypes.string,
  city: PropTypes.string,
  picture: PropTypes.string,
  description: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  activities: PropTypes.array,
  options: PropTypes.array,
  setAcquisitions: PropTypes.func,
  acquisitions: PropTypes.object,
  hostingName: PropTypes.string,
  hostingStars: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stepNumber: PropTypes.number,
  stepTotal: PropTypes.number,
  dateLabel: PropTypes.string,
};

export default ModalTripTimelineItem;
